/**
 * Settings API client
 *
 * Calls /api/settings via the CSG Gateway (NOT direct to agent-backend).
 * The gateway proxies to agent-backend and injects its own X-API-Key so
 * agent-backend's admin-key gate is satisfied — the SPA never sees the
 * admin key.
 *
 * Production: api.csgcloud.co.uk → Caddy → gateway → agent-backend
 * Local dev:  localhost:3000      → gateway → agent-backend
 *
 * Auth from caller: MSAL Bearer token. Since #342 the gateway validates it and
 * requires the Admin role before injecting the admin key, so an expired cached
 * token now matters — settingsFetch refreshes it once on a 401, like gateway.js.
 */

import { refreshAccessToken } from './tokenRefresh.js';

// Settings ALWAYS go via the gateway so its /api/settings* proxy can inject
// X-API-Key (the SPA must never see the admin key). Do NOT fall back to
// VITE_AGENT_BACKEND_URL — that points at agent-backend directly (Traefik
// strips the prefix locally, Caddy in prod) which skips the key injection
// and 403s on PUT/POST.
export const SETTINGS_API_URL =
  import.meta.env.VITE_CSG_GATEWAY_URL || 'http://localhost:3000';

// Backwards-compat alias — some callers may still import AGENT_BACKEND_URL
export const AGENT_BACKEND_URL = SETTINGS_API_URL;

/**
 * Returns headers for /api/settings calls (via gateway).
 */
export function getAgentAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// Keep unexported alias for internal use
const getAuthHeaders = getAgentAuthHeaders;

/**
 * fetch() for the admin-gated /api/settings* routes. On a 401 (expired or missing
 * token) it asks MSAL for a fresh token once, persists it, and retries the same
 * request. A 403 is returned as-is: the caller is signed in but not Admin, and a
 * refresh can't change that.
 */
async function settingsFetch(url, init = {}) {
  const response = await fetch(url, { ...init, headers: getAuthHeaders() });
  if (response.status !== 401) return response;

  const freshToken = await refreshAccessToken();
  if (!freshToken) return response;
  localStorage.setItem('accessToken', freshToken);
  return fetch(url, { ...init, headers: getAuthHeaders() });
}

/**
 * GET /api/settings
 * Returns current LLM provider config. API key is masked (only last 4 chars shown).
 */
export async function getSettings() {
  const response = await settingsFetch(`${SETTINGS_API_URL}/api/settings`);
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`GET /api/settings failed (${response.status}): ${text}`);
  }
  return response.json();
}

/**
 * PUT /api/settings
 * Updates provider config in DB. Takes effect within 30 seconds (cache TTL).
 *
 * @param {Object} settings
 * @param {string} settings.llm_provider - "ollama" | "azure" | "foundry"
 * @param {Object} [settings.ollama] - { base_url, model }
 * @param {Object} [settings.azure] - { endpoint, deployment, api_version, api_key? }
 * @param {Object} [settings.foundry] - { endpoint, model, api_key? } — Azure AI Foundry Anthropic-compatible
 */
export async function updateSettings(settings) {
  const response = await settingsFetch(`${SETTINGS_API_URL}/api/settings`, {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`PUT /api/settings failed (${response.status}): ${text}`);
  }
  return response.json();
}

/**
 * POST /api/settings/test
 * Tests connectivity for the proposed (or current stored) provider config.
 *
 * @param {Object} config - Optional config to test (uses stored config if omitted)
 * @param {string} [config.provider] - "ollama" or "azure"
 * @param {Object} [config.azure] - { endpoint, deployment, api_version, api_key? }
 * @param {Object} [config.ollama] - { base_url, model }
 */
export async function testLLMConnection(config = {}) {
  const response = await settingsFetch(`${SETTINGS_API_URL}/api/settings/test`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`POST /api/settings/test failed (${response.status}): ${text}`);
  }
  return response.json();
}
